import { useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";

import { INIT_FILTER_VALUES } from "../../constants";

/**
 * Custom hook responsible for controlling the state related to the
 * Map filter controls
 * Accepts one argument, `onFilterChange` that is a callback that
 * can be run whenever a filter value changes
 */
const useFilters = ({ onFilterChange, isMapLoaded }) => {
  const [filterValues, setFilterValues] = useState(INIT_FILTER_VALUES);
  const [defaultFilterValues, setDefaultFilterValues] = useState(null);

  /**
   * Hit the API and get a list of options for each filter
   * The endpoint returns an object where each key/value pair
   * represents the options for a specific filter
   * This approach allows us to make a single API request instead
   * of multiple
   */
  const { data } = useQuery(
    ["filterData"],
    async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/filters`
        );
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  /**
   * Set the options and initial value for each filter based on
   * what is returned from the DB
   */
  useEffect(() => {
    if (isMapLoaded && data?.data) {
      const newFilterValues = {
        ...filterValues,
        wellClass: {
          ...filterValues.wellClass,
          options: data?.data?.wellClass || [],
          value: data?.data?.wellClass.map(({ value }) => value) || [],
        },
        wellSubclass: {
          ...filterValues.wellSubclass,
          options: data?.data?.wellSubclass || [],
          value: data?.data?.wellSubclass.map(({ value }) => value) || [],
        },
        monitoringStatus: {
          ...filterValues.monitoringStatus,
          options: data?.data?.monitoringStatus || [],
          value: data?.data?.monitoringStatus.map(({ value }) => value) || [],
        },
        primaryUse: {
          ...filterValues.primaryUse,
          options: data?.data?.primaryUse || [],
          value: data?.data?.primaryUse.map(({ value }) => value) || [],
        },
        managementZone: {
          ...filterValues.managementZone,
          options: data?.data?.managementZone || [],
          value: data?.data?.managementZone.map(({ value }) => value) || [],
        },
        aquiferCompletion: {
          ...filterValues.aquiferCompletion,
          options: data?.data?.aquiferCompletion || [],
          value: data?.data?.aquiferCompletion.map(({ value }) => value) || [],
        },
        aquiferSetting: {
          ...filterValues.aquiferSetting,
          options: data?.data?.aquiferSetting || [],
          value: data?.data?.aquiferSetting.map(({ value }) => value) || [],
        },
      };
      onFilterChange(newFilterValues);
      setFilterValues(newFilterValues);
      setDefaultFilterValues(newFilterValues);
    }
  }, [data?.data, isMapLoaded]); //eslint-disable-line

  const handleResetAll = () => {
    onFilterChange(defaultFilterValues);
    setFilterValues(defaultFilterValues);
  };

  const handleSelectAll = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: data?.data?.[name]?.map(({ value }) => value),
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleSelectNone = (name) => {
    setFilterValues((prevState) => {
      const newState = {
        ...prevState,
        [name]: {
          ...prevState[name],
          value: [],
        },
      };
      onFilterChange(newState);
      return newState;
    });
  };

  const handleFilterValues = (event) => {
    const { checked, name, value } = event.target;

    const type = filterValues[name].type;

    if (type === "multi-select") {
      setFilterValues((prevState) => {
        const newValue = [...prevState[name].value];
        const existingIndex = newValue.indexOf(value);
        if (existingIndex > -1) {
          newValue.splice(existingIndex, 1);
        } else {
          newValue.push(value);
        }
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: newValue,
          },
        };
        onFilterChange(newState);
        return newState;
      });
    } else if (type === "boolean") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: checked,
          },
        };
        onFilterChange(newState);
        return newState;
      });
    } else if (type === "select") {
      setFilterValues((prevState) => {
        const newState = {
          ...prevState,
          [name]: {
            ...prevState[name],
            value: value,
          },
        };
        // onFilterChange(newState);
        return newState;
      });
    }
  };

  return {
    filterValues,
    handleFilterValues,
    handleSelectAll,
    handleSelectNone,
    handleResetAll,
  };
};

export default useFilters;
